import { call, put, select, takeLatest } from 'redux-saga/effects'
import { getDestinationAddressSelector } from '../selectors/destinationAddress.selectors'
import {
  setAddressLine1,
  setAddressLine2,
  setAptSuiteRoom,
  setCensusTract,
  setCity,
  setCountry,
  setCounty,
  setCrossStreetDirections,
  setLocation,
  setMilePostMajorRoadway,
  setOtherType,
  setReceivingGPSE,
  setReceivingGPSN,
  setReceivingUSNG,
  setState,
  setStateIds,
  setZip,
} from '../store/destinationAddress.actions'
import { DestinationAddressConstants } from '../store/destinationAddress.constants'
import { IDispositionState } from '../store/destinationAddress.types'
import { IPickupAddressOptionsState } from '../../options/store/options.types'
import { getServiceIdSelector } from '../../user/selectors/getServiceId.selector'
import { OptionsService } from '../../options/services/options.service'
import { ICity, ICountyOption, IStateAndCountiesOptionsResponse } from '../../../common/common.types'
import { getPickupAddressOptionsSelector } from '../../options/selectors/pickupAddressOptions.selector'
import { ICommonAddressesOption } from '../../pickupAddress/store/pickupAddress.types'
import { initialCommonAddressOption } from '../../pickupAddress/store/pickupAddress.reducers'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { calculateReceivingUSNG } from './setCalculateReceivingUSNG.saga'
import { IPcr } from '../../pcr/store/pcr.types'

export function* setRecCommonAddressValues() {
  const { destination }: IDispositionState = yield select(getDestinationAddressSelector)
  const { commonAddress } = destination
  if (!commonAddress) yield call(setCommonAddressData, initialCommonAddressOption)
  else yield call(setCommonAddressData, commonAddress)
}

export function* setCommonAddressData(commonAddress: ICommonAddressesOption, pcr?: IPcr) {
  try {
    const serviceId: string = yield select(getServiceIdSelector)
    const pickupAddressOptionsState: IPickupAddressOptionsState = yield select(getPickupAddressOptionsSelector)
    const { stateOptions, countryOptions, otherTypeOptions } = pickupAddressOptionsState
    const {
      name,
      type,
      address1,
      address2,
      city,
      state,
      stateIds,
      zip,
      aptSuiteRoom,
      milePostMajorRoadway,
      crossStreet,
      county,
      country,
      censusTract,
      usng,
      matchlat,
      matchlon,
    } = commonAddress

    const typeOption = otherTypeOptions.find((typeOption) => typeOption.id === type) || null

    let cityObj: ICity | null = null
    if (city) cityObj = { name: city, nemsisCode: '' }

    const stateOption = stateOptions.find((stateOption) => stateOption.id === state) || null

    let countyOption: ICountyOption | null = null
    if (county && state) {
      const optionsService = new OptionsService(serviceId)

      const response: IStateAndCountiesOptionsResponse = yield call([optionsService, optionsService.getCountyOptions])
      const { entity } = response

      const filteredCounties = entity.find((obj) => obj.state === String(state))
      countyOption = filteredCounties?.counties.find((option) => option.id === county) || null
    }

    const countryOption = countryOptions.find((countryOption) => countryOption.id === country) || null

    let uSNG = usng || ''
    if (matchlat && matchlon) uSNG = yield call(calculateReceivingUSNG, matchlat, matchlon)

    if (pcr?.pcrId) {
      pcr.disposition = {
        ...pcr.disposition,
        destination: {
          ...pcr.disposition.destination,
          name: name || '',
          txToCode: typeOption,
          addressLine1: address1 || '',
          addressLine2: address2 || '',
          aptSuiteRoom: aptSuiteRoom || '',
          milePostMajorRoadway: milePostMajorRoadway || '',
          crossStreetDirections: crossStreet || '',
          city: cityObj,
          state: stateOption,
          county: countyOption,
          country: countryOption,
          zip: zip || '',
          usng: uSNG,
          censusTract: censusTract || '',
          stateIds,
          gpsn: matchlat || '',
          gpse: matchlon || '',
        },
      }
      return
    }

    yield put(setLocation(name || ''))

    yield put(setOtherType(typeOption))

    yield put(setAddressLine1(address1 || ''))
    yield put(setAddressLine2(address2 || ''))

    yield put(setAptSuiteRoom(aptSuiteRoom || ''))

    yield put(setMilePostMajorRoadway(milePostMajorRoadway || ''))

    yield put(setCrossStreetDirections(crossStreet || ''))

    yield put(setCity(cityObj))

    yield put(setState(stateOption))

    yield put(setCounty(countyOption))

    yield put(setZip(zip || ''))

    yield put(setCountry(countryOption))

    yield put(setCensusTract(censusTract || ''))

    yield put(setReceivingUSNG(usng || ''))

    yield put(setStateIds(stateIds))

    if (matchlat && matchlon) {
      yield put(setReceivingGPSN(matchlat))
      yield put(setReceivingGPSE(matchlon))
      yield put(setReceivingUSNG(uSNG || ''))
    } else {
      yield put(setReceivingGPSN(''))
      yield put(setReceivingGPSE(''))
    }
  } catch (e) {
    yield put(throwPcrException(`Destination Address - setRecCommonAddressData() error: ${e}`))
  }
}

export function* setRecCommonAddressValuesSaga() {
  yield takeLatest([DestinationAddressConstants.SET_COMMON_ADDRESS], performWithMutexHOG, setRecCommonAddressValues)
}
